import React, { FC, useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Collapse,
    Container,
    Form,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import {
    BatchBuild,
    BatchBuildData,
    BatchBuildState,
    BatchBuildVariables,
    BuildIdVariable,
    NetworkBuild,
    ReadyNotificationData,
    ReleaseType,
    ReportDataV2,
    Revision
} from "../../models/types";
import {
    Link,
    useLocation,
    useNavigate,
    useNavigationType,
    useParams
} from "react-router-dom";
import { useLazyQuery, useQuery, useSubscription } from "@apollo/client";
import {
    GET_NETWORK_BUILDS_WITH_FILTER,
    GET_RESULT
} from "../../graphql/queries";
import { NetworkBuildReport } from "../simple/NetworkBuildReport";
import { SearchBar } from "../search/SearchBar";
import { BackButton } from "../simple/BackButton";
import { useLocalState } from "../../graphql/hooks";
import {
    faCircleInfo,
    faExclamationCircle,
    faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    getFormattedTime,
    loadBuildPlayState,
    saveMutatorConfigLocal,
    tryRemoveKeyFromLocalStorage
} from "../../common/Helpers";
import { LegacyLinkPageButton } from "../buttons/LegacyLinkPageButton";
import { SimpleToast } from "../simple/SimpleToast";
import { BuildLogsModal } from "../modals/BuildLogsModal";
import { ZipDownloadButton } from "../buttons/ZipDownloadButton";
import { Paginator } from "../lists/Paginator";
import { READY_SUBSCRIPTION } from "../../graphql/subscriptions";
import { UpButton } from "../buttons/UpButton";
import cx from "classnames";
import styles from "./BuildReport.module.scss";

interface Props {
    latest: BatchBuild;
    revision: Revision;
}

export const BuildReport: FC<Props> = ({ latest, revision }) => {
    const { jobId } = useParams<{
        jobId: string;
    }>();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const state = useLocalState();
    const [showHelp, setShowHelp] = useState(false);
    const toggleShowHelp = () => setShowHelp(!showHelp);
    const [showLogsModal, updateLogsModal] = useState(false);
    const [openModalTo, updateOpenModalTo] = useState<{
        job: undefined | string;
        id: undefined | number;
        buildId: undefined | number;
    }>({
        job: undefined,
        id: undefined,
        buildId: undefined
    });
    const [networkBuilds, updateNetworkBuilds] = useState<NetworkBuild[]>([]);
    const [showBuilds, setShowBuilds] = useState(true);
    const [showFullBuildName, setShowFullBuildName] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [searchData, setSearchData] = useState<string>("");
    const [isPortrait, setIsPortrait] = useState(
        window.innerWidth < window.innerHeight
    );
    const checkAspectRatio = () => {
        setIsPortrait(window.innerWidth < window.innerHeight);
    };
    const { data: { build } = {} } = useQuery<
        BatchBuildData,
        BatchBuildVariables
    >(GET_RESULT, {
        variables: { id: Number(jobId) },
        fetchPolicy: "network-only"
    });

    const [getNetworkBuilds, { data }] = useLazyQuery<{
        networkBuildsWithFilter: NetworkBuild[];
    }>(GET_NETWORK_BUILDS_WITH_FILTER, { fetchPolicy: "network-only" });

    useSubscription<ReadyNotificationData, BuildIdVariable>(
        READY_SUBSCRIPTION,
        {
            variables: { id: Number(jobId) },
            onData: ({
                data: { data: { readyNotification } = {} },
                client
            }) => {
                const currentReportData = client.cache.readQuery<
                    BatchBuildData,
                    BatchBuildVariables
                >({
                    query: GET_RESULT,
                    variables: { id: Number(jobId) }
                });

                if (
                    currentReportData &&
                    readyNotification &&
                    readyNotification.success
                ) {
                    const newBuildData = { ...currentReportData.build };
                    const type = readyNotification.type;
                    switch (type) {
                        case "legacyLink":
                            newBuildData.legacyLinkUrl = readyNotification.link;
                            break;
                        case "zip":
                            newBuildData.zipUrl = readyNotification.link;
                            break;
                        default:
                            console.log("[DEBUG] type not implemented ", type);
                            break;
                    }
                    client.cache.writeQuery<
                        BatchBuildData,
                        BatchBuildVariables
                    >({
                        query: GET_RESULT,
                        variables: { id: Number(jobId) },
                        data: {
                            ...currentReportData,
                            build: newBuildData
                        }
                    });
                }
            }
        }
    );

    const initialPlayedHistory = useMemo(() => {
        return loadBuildPlayState(Number(jobId));
    }, []);

    useEffect(() => {
        if (data && data.networkBuildsWithFilter) {
            const builds = [...data.networkBuildsWithFilter];
            updateNetworkBuilds(builds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        getNetworkBuilds({
            variables: {
                buildId: Number(jobId),
                searchValue: searchValue,
                searchData: searchData,
                sortBy: "id"
            }
        });
    }, [jobId, getNetworkBuilds, searchValue, searchData]);

    useEffect(() => {
        window.addEventListener("resize", checkAspectRatio);
        checkAspectRatio();

        const cleanup = () =>
            window.removeEventListener("resize", checkAspectRatio);

        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (
        !build ||
        build.reportData === undefined ||
        build.reportData === null ||
        !networkBuilds
    ) {
        return (
            <Container className="fullSize p-2 pt-5 text-center text-contrast">
                <h5>Build report data not found</h5>
            </Container>
        );
    }

    const isLatest = build.id === latest.id;

    const openModal = (
        job: string,
        id: number,
        buildId: number | undefined
    ) => {
        updateOpenModalTo({ job: job, id: id, buildId: buildId });
        updateLogsModal(true);
    };

    const goToCustomator = () => {
        if (build.mutatorConfig) {
            console.log(
                "[DEBUG] setting mutator config: ",
                build.mutatorConfig
            );
            saveMutatorConfigLocal(revision.id, build.mutatorConfig);
        } else {
            console.log("[DEBUG] forcing default mutator config ");
            tryRemoveKeyFromLocalStorage("mutator-config-" + revision.id);
        }
        navigate(mutatorUrl);
    };

    const reports = networkBuilds?.map(
        (networkBuild: NetworkBuild, index: number) => (
            <NetworkBuildReport
                key={index}
                buildId={Number(build.id)}
                networkBuild={networkBuild}
                localizations={build.reportData.localizations}
                duplicates={undefined}
                statsKey={`${build.reportData.info.prefix}/${networkBuild.statsFile}`}
                s3Key={`${build.reportData.info.prefix}/${networkBuild.filename}`}
                openModal={openModal}
                arrayIndex={index}
                buildType={build.buildType}
                showFullBuildName={showFullBuildName}
                buildPlayHistory={initialPlayedHistory}
                cardColor={"inner-card-other"}
            />
        )
    );

    const historyPath = location.pathname.substring(
        0,
        location.pathname.lastIndexOf("/")
    );
    const dateTime = getFormattedTime(build.dateUpdated);
    const editPanelUrl = `/products/${revision.project.product?.id}/projects/${revision.project.id}/revisions/${revision.id}/variations`;
    const mutatorUrl = `/products/${revision.project.product?.id}/projects/${revision.project.id}/revisions/${revision.id}/variations/customator`;

    return (
        <Container
            className={`pt-1 ${
                state.developerMode ? "pageMaxWidthDev" : "pageMaxWidthClient"
            }`}
        >
            <Row className="m-0 p-0 pt-3">
                <Col className="mt-1 col-1">
                    <BackButton
                        hasHistory={!(location.key && navigationType === "POP")}
                        goBack={() => navigate(-1)}
                        size={"lg"}
                    />
                </Col>
                <Col className="float-end text-end mb-1 col-11">
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 550, hide: 200 }}
                        overlay={
                            <Tooltip id="build-report-customator-tooltip">
                                Go to Customator with the configuration used in
                                this build
                            </Tooltip>
                        }
                    >
                        <Button
                            variant="primary"
                            className="mb-1"
                            onClick={() => goToCustomator()}
                        >
                            Customator
                        </Button>
                    </OverlayTrigger>
                    {(state.developerMode ||
                        revision.releaseType === ReleaseType.ClientRelease) && (
                        <Link to={editPanelUrl}>
                            <Button className="ms-2 mb-1">Build Edit</Button>
                        </Link>
                    )}
                    <Link to={historyPath}>
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 550, hide: 200 }}
                            overlay={
                                <Tooltip id="build-report-history-tooltip">
                                    Go to list of all builds generated for{" "}
                                    {build.revision.name}
                                </Tooltip>
                            }
                        >
                            <Button className="mb-1 ms-2">Build History</Button>
                        </OverlayTrigger>
                    </Link>

                    {state.developerMode &&
                    (build.reportData as ReportDataV2)?.version === 2 ? (
                        <Button
                            onClick={() => {
                                updateOpenModalTo({
                                    job: undefined,
                                    id: undefined,
                                    buildId: undefined
                                });
                                updateLogsModal(true);
                            }}
                            className="btn-primary text-end mb-1 ms-2"
                        >
                            Logs
                        </Button>
                    ) : null}
                    {build.analyticsSchema && state.developerMode ? (
                        <Button
                            onClick={() =>
                                navigate(`${location.pathname}/analyticsSchema`)
                            }
                            className="btn-primary mb-1 text-end ms-2"
                        >
                            Analytics Schema
                        </Button>
                    ) : null}
                </Col>
            </Row>

            <Card className="text-contrast m-2">
                <Card.Header className="d-flex align-items-center">
                    {state.developerMode ? (
                        <h5 className="p-0">
                            {build.revision.project.product?.name} /{" "}
                            {build.revision.project.name} /{" "}
                            {build.revision.name}
                        </h5>
                    ) : (
                        <h5>
                            Build report for{" "}
                            <span className="text-purple">
                                {build.revision.name}{" "}
                                {build.name && ` / ${build.name}`}
                            </span>
                        </h5>
                    )}
                    {state.developerMode ? (
                        <div className="ms-1 mb-2">
                            {isLatest ? (
                                <Badge bg="success" className="mx-1">
                                    Latest
                                </Badge>
                            ) : latest.state === BatchBuildState.Started ? (
                                <Badge bg="info" className="mx-1">
                                    Newer Building
                                </Badge>
                            ) : (
                                <Badge bg="warning" className="mx-1">
                                    Outdated
                                </Badge>
                            )}
                            {/*// @ts-ignore*/}
                            {(build.reportData as ReportDataV2)
                                ?.pipelineWarnings?.length > 0 ? (
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    size={"lg"}
                                    className={"text-warning ms-2"}
                                />
                            ) : null}
                            {/*// @ts-ignore*/}
                            {(build.reportData as ReportDataV2)?.pipelineErrors
                                ?.length > 0 ? (
                                <FontAwesomeIcon
                                    icon={faExclamationCircle}
                                    size={"lg"}
                                    className={"text-danger ms-2"}
                                />
                            ) : null}
                        </div>
                    ) : null}
                </Card.Header>
                <Card.Body className="p-2 pt-2 pb-2">
                    <Row>
                        <Col>
                            <div className="p-2 text-subtle bread-text">
                                <p>
                                    {build.name ? (
                                        <>
                                            Name: {build.name} <br />
                                        </>
                                    ) : null}
                                    By:{" "}
                                    {build.user?.displayName ?? "unknown user"}{" "}
                                    <br />
                                    Build ID: {build.id} <br />
                                    Finished: {dateTime} <br />
                                    {build.reportData.localizations
                                        ? "Localizations: " +
                                          build.reportData.localizations?.join(
                                              ", "
                                          )
                                        : null}{" "}
                                    <br />
                                </p>
                            </div>
                        </Col>
                        <Col className="d-flex justify-content-end me-2 align-items-center">
                            {state.developerMode && (
                                <div className="me-2">
                                    <LegacyLinkPageButton
                                        jobId={Number(jobId)}
                                        legacyLink={build.legacyLinkUrl}
                                        zip={build.zipUrl}
                                        pathTemplate={build.pathTemplate}
                                    />
                                </div>
                            )}
                            <ZipDownloadButton
                                buildId={Number(build.id)}
                                zip={build.zipUrl}
                                isLargeBuild={
                                    (networkBuilds &&
                                        networkBuilds.length > 50) ||
                                    false
                                }
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            xs={12}
                            lg={isPortrait ? 12 : 2}
                            className="pe-lg-0 pe-xs-4"
                        >
                            <SearchBar
                                info={build.reportData.info}
                                onSearchChange={setSearchValue}
                                onSearchDataChange={setSearchData}
                            />
                        </Col>
                        <Col
                            xs={12}
                            lg={isPortrait ? 12 : 10}
                            className="ps-xs-4"
                        >
                            <Card className="text-contrast inner-card">
                                <Card.Header
                                    className="px-3 pt-2 pb-1"
                                    onClick={() => setShowBuilds(!showBuilds)}
                                >
                                    <Row className="g-0">
                                        <Col>
                                            <h5 className="text-subtle">
                                                {state.developerMode
                                                    ? "Builds"
                                                    : "Ad versions"}
                                            </h5>
                                        </Col>
                                        {!state.developerMode && (
                                            <Col
                                                xs="auto"
                                                className="float-end ms-auto text-disabled ps-2 pt-1"
                                            >
                                                <OverlayTrigger
                                                    placement="top"
                                                    delay={{
                                                        show: 550,
                                                        hide: 200
                                                    }}
                                                    overlay={
                                                        <Tooltip id="build-report-help-tooltip">
                                                            {showHelp
                                                                ? "Hide Help"
                                                                : "Show Help"}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCircleInfo}
                                                        onClick={toggleShowHelp}
                                                        className={cx(
                                                            styles.infoIcon,
                                                            `mouseHover ms-2 ${
                                                                showHelp
                                                                    ? "text-subtle"
                                                                    : "text-disabled"
                                                            }`
                                                        )}
                                                    />
                                                </OverlayTrigger>
                                            </Col>
                                        )}
                                    </Row>
                                </Card.Header>
                                <Card.Body className="p-2">
                                    <Row className="align-items-center no-wrap text-subtle mb-2 me-1">
                                        <Col>
                                            <OverlayTrigger
                                                placement="top"
                                                delay={{ show: 550, hide: 200 }}
                                                overlay={
                                                    <Tooltip id="build-report-filename-toggle-tooltip">
                                                        {showFullBuildName
                                                            ? "Show only ad version name and duration"
                                                            : "Show full filename"}
                                                    </Tooltip>
                                                }
                                            >
                                                <Form.Check
                                                    type="switch"
                                                    id="build-name-switch"
                                                    label="Full titles"
                                                    className="ms-3 bread-text text-subtle"
                                                    checked={showFullBuildName}
                                                    onChange={e =>
                                                        setShowFullBuildName(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            </OverlayTrigger>
                                        </Col>
                                    </Row>
                                    {!state.developerMode ? (
                                        <Collapse in={showHelp}>
                                            <div>
                                                <Card.Footer className="bread-text no-border text-subtle mb-3 pe-2">
                                                    <>
                                                        <p>
                                                            The{" "}
                                                            <b>Ad versions</b>{" "}
                                                            list shows the
                                                            different variations
                                                            of your ad,
                                                            generated based on
                                                            the configuration.
                                                            From here, you can
                                                            share any version
                                                            using different
                                                            types of links.
                                                        </p>
                                                        <p>
                                                            To preview an ad,
                                                            click the play
                                                            button and choose a
                                                            localization (or use
                                                            the default).
                                                            Selecting a specific
                                                            language will
                                                            override the default
                                                            localization. HTML
                                                            builds open in a new
                                                            browser tab, while
                                                            other formats are
                                                            downloaded as a zip
                                                            file. You can copy
                                                            and share the link
                                                            directly from your
                                                            browser. Note that
                                                            preview links are{" "}
                                                            <b>
                                                                valid for 1 hour
                                                            </b>{" "}
                                                            and stored
                                                            privately.
                                                        </p>
                                                        <p>
                                                            The QR code button
                                                            generates a
                                                            temporary shareable
                                                            link (also valid for
                                                            1 hour) — handy for
                                                            quickly sharing the
                                                            ad, for example to a
                                                            mobile device.
                                                        </p>
                                                        <p>
                                                            You can also
                                                            download all
                                                            versions at once as
                                                            a zip file using the
                                                            button above the
                                                            builds list.
                                                        </p>
                                                    </>
                                                </Card.Footer>
                                            </div>
                                        </Collapse>
                                    ) : null}
                                    <Paginator
                                        key={
                                            reports.length > 0
                                                ? "forceUpdate"
                                                : "whenEmpty"
                                        }
                                        list={reports}
                                        maxElementCount={50}
                                        activeItem={undefined}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            <Row className="p-0 m-0 mt-3 justify-content-center mb-2">
                {networkBuilds.length > 20 && <UpButton />}
            </Row>
            <SimpleToast />
            {showLogsModal && state.developerMode ? (
                <BuildLogsModal
                    batchBuildId={Number(build.id)}
                    show={showLogsModal}
                    onClose={() => updateLogsModal(false)}
                    job={openModalTo.job}
                    id={openModalTo.id}
                    targetIndex={openModalTo.buildId}
                    buildReport={build.reportData}
                />
            ) : null}
        </Container>
    );
};
